import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Image from 'utils/image';
import Panel from 'utils/panel';
import IconClose from '../utils/icon/icon-close.svg';

const SizeGuide = ({ sizeGuideData, subBrand, product, description }) => {
  const closeTriggerEl = useRef(null);
  const categories = useMemo(() => {
    return [
      {
        name: 'Period',
        scheme: 'color-scheme-3',
      },
      {
        name: 'Bladder',
        scheme: 'color-scheme-4',
      },
      {
        name: 'Teens',
        scheme: 'color-scheme-5',
      },
    ];
  }, []);
  const [activeTab, setActiveTab] = useState(subBrand ?? 'Period');
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: null,
    closeTriggerEl: null,
  });

  useEffect(() => {
    const openTriggerEl = document.querySelector('[data-size-guide-trigger]');

    setPanelTriggers(() => ({
      openTriggerEl,
      closeTriggerEl: closeTriggerEl.current,
    }));

    const tabs = document.querySelectorAll('[role="tab"]');
    let currentIndex = 0;

    tabs.forEach((tab, index) => {
      tab.addEventListener('keydown', ({ key }) => {
        if (key === 'ArrowRight') {
          currentIndex = (index + 1) % tabs.length;
        } else if (key === 'ArrowLeft') {
          currentIndex = (index - 1 + tabs.length) % tabs.length;
        } else {
          return;
        }

        tabs[currentIndex].focus();
        tabs[currentIndex].setAttribute('aria-selected', 'true');
        tabs[currentIndex].removeAttribute('tabindex');

        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });
    });
  }, []);

  const sizeGuide = useMemo(() => {
    if (!sizeGuideData) {
      return false;
    }
    return sizeGuideData.find((item) => item.category === activeTab);
  }, [activeTab, sizeGuideData]);

  const colorScheme = useMemo(() => categories.find((item) => item.name === activeTab)?.scheme, [activeTab]);
  const shouldShowProductDescription = product.metafields.subBrand === activeTab;

  return (
    <Panel
      triggers={panelTriggers}
      overlay={true}
      additionalClasses="flex flex-col max-w-[38.75rem] z-50"
    >
      <div className="flex items-center justify-between px-3 pb-3 pt-5 lg:px-8">
        <h2 className="heading-4 capitalize">{window.TMW.Strings.sizeGuide.title}</h2>
        <button
          className="[&>svg]:size-6"
          ref={closeTriggerEl}
          aria-label={window.TMW.Strings.accesssibility.close}
        >
          <IconClose />
        </button>
      </div>
      <div className="h-[calc(100%-58px)] overflow-y-auto px-3 py-5 lg:h-[calc(100%-65px)] lg:px-8">
        <ul
          className="flex items-center justify-center gap-8"
          role="tablist"
        >
          {categories.map((tab) => (
            <li key={tab.name}>
              <button
                id={`${tab.name.toLowerCase()}-tab`}
                tabIndex={activeTab !== tab.name && -1}
                role="tab"
                aria-controls={`${tab.name.toLowerCase()}-size-chart-tabpanel`}
                aria-selected={activeTab === tab.name}
                className={`heading-5 ${activeTab === tab.name ? 'menu-item before:w-full before:opacity-100' : ''}`}
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.name}
              </button>
            </li>
          ))}
        </ul>
        <div
          id={`${activeTab.toLowerCase()}-size-chart-tabpanel`}
          role="tabpanel"
          aria-labelledby={`${activeTab.toLowerCase()}-tab`}
        >
          {shouldShowProductDescription && (
            <div className={`mt-10 flex h-[9.375rem] w-full overflow-hidden rounded-lg+ lg:h-[7.5rem] ${colorScheme}`}>
              {product?.images?.[0] && (
                <div className="h-full w-[9.375rem] shrink-0 lg:w-[7.5rem]">
                  <Image
                    {...product?.images?.[0]}
                    srcWidths={{ sm: 640 }}
                    classes={clsx('w-full aspect-square object-cover size-full')}
                  />
                </div>
              )}
              <div className="scrollbar-hide flex h-[calc(100%-theme(spacing.5))] flex-1 flex-col self-center overflow-y-auto px-6 lg:h-[calc(100%-theme(spacing.5))]">
                <p className="body-2-bold">{product.title}</p>
                {description && (
                  <div
                    className="rte body-3 [&>div>p]:mb-0 [&>div>ul>li]:!mt-0 [&>div>ul]:m-0 [&>div>ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </div>
            </div>
          )}
          {sizeGuide && (
            <div className="mt-10">
              {!!sizeGuide?.tables?.length && (
                <div className="flex flex-col gap-10">
                  {sizeGuide.tables.map((table) => (
                    <SizingTable
                      key={table.id}
                      data={table}
                    />
                  ))}
                </div>
              )}
              {sizeGuide?.how_to_measure_above && (
                <div
                  className="body-3 mt-4"
                  dangerouslySetInnerHTML={{ __html: sizeGuide?.how_to_measure_above }}
                ></div>
              )}
              <div className="mt-10 grid grid-cols-1 gap-16 lg:grid-cols-[276px_auto]">
                {sizeGuide?.how_to_measure_below && (
                  <div
                    className="rte [&>div>p]:body-3 [&>div>h4]:mb-2"
                    dangerouslySetInnerHTML={{ __html: sizeGuide?.how_to_measure_below }}
                  ></div>
                )}
                {sizeGuide?.image?.src && (
                  <div className="flex justify-center">
                    <Image
                      {...sizeGuide.image}
                      srcWidths={{ sm: 640 }}
                      classes="w-full h-auto object-cover rounded-[4px]"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};

export default SizeGuide;

const SizingTable = ({ data }) => {
  const units = ['in', 'cm'];
  const [activeTab, setActiveTab] = useState('in');
  const table = useMemo(() => {
    if (!data?.standard && !data?.metric) return [];
    return activeTab === 'in' ? data?.standard : data?.metric;
  }, [data, activeTab]);
  const tableHeadings = useMemo(() => table && table[0] && Object.keys(table[0]), [table]);
  const isActiveTab = (unit) => activeTab === unit;
  const handleKeyDown = (code, unit) => (code === 'Enter' || code === 'Space') && setActiveTab(unit);

  return (
    <div className="overflow-hidden">
      <div className="mb-5 flex items-center justify-between">
        {data?.title ? <p className="heading-4">{data.title}</p> : <span></span>}
        {data?.standard && data?.metric && (
          <div
            role="radiogroup"
            className="relative flex self-end rounded-full border border-grey-medium"
            aria-labelledby={`${data.id}-label`}
          >
            <span
              id={`${data.id}-label`}
              className="sr-only"
            >
              {data.title} Unit
            </span>
            {units.map((unit) => (
              <label
                key={unit}
                htmlFor={`${data.id}-${unit}`}
                className="relative cursor-pointer"
              >
                <input
                  type="radio"
                  id={`${data.id}-${unit}`}
                  className="sr-only"
                  tabIndex={-1}
                  checked={isActiveTab(unit)}
                  aria-checked={isActiveTab(unit)}
                  onChange={() => setActiveTab(unit)}
                />
                <span
                  className={clsx(
                    'body-3-bold inline-flex size-7.5 cursor-pointer items-center justify-center rounded-full px-2 transition-colors duration-200',
                    {
                      'bg-black text-white hover:bg-brand-tangerine': isActiveTab(unit),
                    },
                  )}
                  tabIndex={0}
                  aria-checked={isActiveTab(unit)}
                  onKeyDown={({ code }) => handleKeyDown(code, unit)}
                >
                  {window.TMW.Strings.sizeGuide[unit]}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
      {table?.length > 0 && (
        <div
          className="overflow-auto"
          tabIndex={0}
        >
          <table className="w-full table-fixed border-collapse text-center">
            <tr>
              {tableHeadings?.map((item, index) => (
                <th
                  key={index}
                  scope='col'
                  className={clsx('body-1-bold border border-white bg-grey-light px-1 py-5 lg:w-[138.67px] lg:px-5.5', {
                    'lg:w-[140px]': index === 0,
                  })}
                >
                  {index !== 0 && item}
                </th>
              ))}
            </tr>
            {table.map((row, rowIndex) => (
              <tr
                className="body-1 bg-white text-center"
                key={rowIndex}
              >
                {tableHeadings?.map((item, index) => (
                  <td
                    className={clsx('body-1 border border-white px-5.5 py-5', {
                      'body-1-bold bg-grey-light': index === 0,
                    })}
                    key={index}
                  >
                    {row[item] ? row[item] : ''}
                  </td>
                ))}
              </tr>
            ))}
          </table>
        </div>
      )}
    </div>
  );
};
